import { User } from '@/api/types/public';
import { ReportingCenter } from '@/reporting/reportingCenter';
import { loadSensorsSdk } from '@/reporting/sdkLoader';
import logger from '@/utils/client-logger';
import { ZL_USERINFO } from '@/utils/constants';
import { env } from '@/utils/env';
import { getPlatformLanguage } from '@/utils/locale-cookie';
import { isInZeloolApp } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';
import { syncSensorsIdentityToStorage } from '../utils/sensorsIdentity';

interface SensorsConfig {
  serverUrl: string;
  showLog: boolean;
  platform: string;
  platformName: string;
  envName: string;
  isEnabled?: boolean;
}

interface SnowplowIdentityProvider {
  setBusinessUserId: (userId: string) => void;
}

/** Sensors login callback 的最长等待时间。 */
const SENSORS_LOGIN_CALLBACK_TIMEOUT_MS = 8000;

/**
 * 判断 provider 是否支持 Snowplow business user id 同步。
 */
function isSnowplowIdentityProvider(provider: unknown): provider is SnowplowIdentityProvider {
  return (
    typeof provider === 'object' &&
    provider !== null &&
    'setBusinessUserId' in provider &&
    typeof provider.setBusinessUserId === 'function'
  );
}

export class SensorsProvider implements Provider {
  /** Provider 类型标识。 */
  type = ProviderType.SENSORS;
  /** 当前 Provider 是否启用。 */
  isEnabled: boolean;
  /** 是否输出 Sensors 调试日志，供 ReportingCenter 访问。 */
  loggingEnabled = false;
  /** Sensors SDK Provider 是否已完成初始化。 */
  private initialized = false;
  /** Sensors Provider 初始化过程的 single-flight Promise。 */
  private initializationPromise: Promise<boolean> | null = null;
  /** 按 UUID 保存本页面已创建的登录屏障，避免重复登录调用。 */
  private loginBarriers = new Map<string, Promise<void>>();
  /** 最近一次请求的登录 UUID，用于只复用连续相同身份的屏障。 */
  private lastRequestedLoginUuid: string | null = null;
  /** 串行执行不同 UUID 的身份切换，避免 callback 交叉覆盖。 */
  private loginSequence: Promise<void> = Promise.resolve();
  /** Sensors Provider 初始化配置。 */
  private config: SensorsConfig;

  /** 创建 Sensors Provider。 */
  constructor(config: SensorsConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  /** 幂等启动 Sensors Provider 初始化。 */
  init(): Promise<boolean> {
    // 配置禁用和服务端渲染是确定性不可初始化状态，不创建可重试的 SDK 加载任务。
    if (!this.isEnabled || typeof window === 'undefined') return Promise.resolve(false);
    if (this.initialized) return Promise.resolve(true);

    this.initializationPromise ??= this.initialize();
    const currentInitialization = this.initializationPromise;

    // 仅清理瞬时加载或配置失败的 Promise；成功结果继续作为 single-flight 缓存复用。
    void currentInitialization.then(
      (ready) => {
        if (!ready && this.initializationPromise === currentInitialization) {
          this.initializationPromise = null;
        }
      },
      () => {
        if (this.initializationPromise === currentInitialization) {
          this.initializationPromise = null;
        }
      },
    );
    return currentInitialization;
  }

  /** 执行一次 Sensors SDK 配置与公共属性注册。 */
  private async initialize(): Promise<boolean> {
    try {
      if (!this.isEnabled) return false;
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // 在 SSR Script 尚未就绪时，通过共享加载器兜底加载神策脚本。
      if (!window.sensorsDataAnalytic201505) {
        await loadSensorsSdk();
      }

      // 获取已加载的 SDK 实例并保留兼容别名。
      const sensors = window.sensorsDataAnalytic201505;
      window.sensors = sensors;
      if (!sensors) {
        logger.error('Sensors Analytics SDK not loaded');
        return false;
      }

      // 使用当前站点配置初始化神策 SDK。
      sensors.init({
        server_url: this.config.serverUrl,
        app_js_bridge: isInZeloolApp(),
        is_track_single_page: true, // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
        use_client_time: true,
        send_type: 'beacon',
        heatmap: {
          // 是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
          clickmap: 'default',
          // 是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
          scroll_notice_map: 'default',
        },
        show_log: this.config.showLog,
        batch_send: {
          datasend_timeout: 6000, //一次请求超过多少毫秒的话自动取消，防止请求无响应。
          send_interval: 800, //间隔多少毫秒发一次数据。
          storage_length: 1000, // 存储 localStorage 条数最大值，默认：1000 。如 localStorage 条数超过该值，则使用 image 方式立即发送数据。v1.24.8 以上支持。
        },
      });

      // 注册公共属性并启用现有的自动采集能力。
      this.registerCommonProperties(sensors);

      sensors.use('PageLeave', {});
      sensors.quick('autoTrack');
      this.syncIdentity();

      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Sensors Analytics:', error);
      return false;
    }
  }

  /** 上报 Sensors 业务事件。 */
  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      const sensors = window.sensorsDataAnalytic201505;
      try {
        const _params: Record<string, any> = {
          $url: window.location.href,
        };
        // 根据标准：只要是值为 '', null, undefined, 'null', 'undefined'
        Object.keys(event.properties).forEach((key) => {
          const v = event.properties[key];
          if (
            v !== '' &&
            v !== 'null' &&
            v !== 'undefined' &&
            v !== null &&
            v !== undefined &&
            !(v instanceof Array && !v.length)
          ) {
            _params[key] = v;
          }
        });
        // 神策自动上报链路禁止 event_id，使用 sensors_event_id 承载内部统一事件 ID。
        if (event.eventId) {
          _params.sensors_event_id = event.eventId;
        }
        if (this.loggingEnabled) {
          logger.log('sensorProvider, Event:', event.eventName, _params);
        }
        sensors.track(event.eventName, _params);
      } catch (error) {
        logger.log('sensors track error', error);
      }
    } catch (error) {
      logger.error('Sensors tracking error:', error);
    }
  }

  /** 将 Sensors 用户状态标记为退出。 */
  sensorsLogout() {
    try {
      // window.sensorsDataAnalytic201505.logout();
      window.sensorsDataAnalytic201505.setProfile({ pub_is_login: false });
    } catch (error) {
      logger.log('sensors logout error', error);
    }
  }

  /** 建立或复用指定 UUID 的 Sensors 登录 callback 屏障。 */
  sensorsLogin(uuid: string, snowplowUserId?: string): Promise<void> {
    if (!uuid) return Promise.reject(new Error('Sensors login UUID is required'));

    const existing = this.loginBarriers.get(uuid);
    if (existing && uuid === this.lastRequestedLoginUuid) return existing;
    this.lastRequestedLoginUuid = uuid;

    // 无论前一个身份屏障成功或失败，都继续串行处理下一个 UUID。
    const barrier = this.loginSequence.then(
      () => this.performSensorsLogin(uuid, snowplowUserId),
      () => this.performSensorsLogin(uuid, snowplowUserId),
    );
    this.loginSequence = barrier.catch(() => undefined);
    this.loginBarriers.set(uuid, barrier);

    // 失败屏障只清理自身映射，使后续显式调用同一 UUID 可以重试。
    void barrier.catch(() => {
      if (this.loginBarriers.get(uuid) === barrier) {
        this.loginBarriers.delete(uuid);
      }
    });
    return barrier;
  }

  /** 使用单一总截止时间等待 Provider ready 与 SDK callback，并解除身份屏障。 */
  private async performSensorsLogin(uuid: string, snowplowUserId?: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let sdkLoginStarted = false;

      /** 以唯一出口结束身份屏障并回收总超时计时器。 */
      const fail = (error: unknown) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timer);
        reject(error);
      };

      /** 8 秒总截止时间同时约束 SDK ready 和 login callback 两个阶段。 */
      const timer = globalThis.setTimeout(() => {
        fail(
          new Error(
            sdkLoginStarted ? 'Sensors login callback timed out' : 'Sensors login timed out',
          ),
        );
      }, SENSORS_LOGIN_CALLBACK_TIMEOUT_MS);

      /** 仅允许首次 SDK callback 完成当前身份屏障。 */
      const complete = () => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timer);
        resolve();
      };

      // Provider ready 迟到时必须保持超时后的 settled 状态，禁止再触发 SDK login。
      void this.init().then((ready) => {
        if (settled) return;

        const sensors =
          typeof window === 'undefined' ? undefined : window.sensorsDataAnalytic201505;
        if (!ready || !sensors) {
          fail(new Error('Sensors Provider is not ready for login'));
          return;
        }

        try {
          sdkLoginStarted = true;
          sensors.login(uuid, complete);
          this.syncIdentity();
          this.syncSnowplowBusinessUserId(snowplowUserId);
        } catch (error) {
          fail(error);
        }
      }, fail);
    });
  }

  /** 返回 Sensors SDK Provider 是否已 ready。 */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 同步 Snowplow business user id，失败不阻断神策登录链路。
   */
  private syncSnowplowBusinessUserId(userId?: string): void {
    if (!userId) return;

    try {
      const snowplowProvider = ReportingCenter.getInstance().getProvider(ProviderType.SNOWPLOW);
      if (isSnowplowIdentityProvider(snowplowProvider)) {
        snowplowProvider.setBusinessUserId(userId);
      }
    } catch (error) {
      logger.log('snowplow set business user id error', error);
    }
  }

  /**
   * 将神策 SDK 当前身份同步到本地缓存，供 Snowplow 首屏事件兜底读取。
   */
  private syncIdentity(): void {
    syncSensorsIdentityToStorage();
  }

  /** 注册 Sensors 页面公共属性。 */
  private registerCommonProperties(sensors: any): void {
    // const latestChannelAttribute = this.getLatestChannelAttribute();
    const params = {
      pub_platform: this.config.platform,
      pub_platform_name: this.config.platformName,
      pub_platform_language: () => getPlatformLanguage(),
      pub_client: this.config.platform,
      pub_env_name: () => {
        // 区分灰度的新服务还是旧服务
        if (this.config.envName === 'production') return 'production_is_nextjs';
        return this.config.envName;
      },
      pub_member_level: () => {
        let userInfo: User = {};
        try {
          userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
        } catch (error) {
          logger.error('Failed to parse user info:', error);
        }
        return `${userInfo.member_level >= -1 ? userInfo.member_level : '9999'}`;
      },
      pub_is_paid: () => {
        let userInfo: User = {};
        try {
          userInfo = JSON.parse(localStorage.getItem(ZL_USERINFO) || '{}') || {};
        } catch (error) {
          logger.error('Failed to parse user info:', error);
        }
        return !!userInfo.last_pay_time;
      },
      pub_is_login: () => {
        try {
          return !!localStorage.getItem('zl-token');
        } catch (error) {
          logger.error('Failed to parse user info:', error);
          return false;
        }
      },
      pub_zl_utm_source: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_source || undefined;
        }
        return undefined;
      },
      pub_zl_utm_medium: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_medium || undefined;
        }
        return undefined;
      },
      pub_zl_utm_campaign: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_campaign || undefined;
        }
        return undefined;
      },
      pub_zl_utm_campaignid: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_utm_campaignid || undefined;
        }
        return undefined;
      },
      pub_zl_referrer: () => {
        if (window.latestChannelAttribute?.expires > Date.now()) {
          return window.latestChannelAttribute?.pub_zl_referrer || undefined;
        }
        return undefined;
      },
      pub_zl_page: () => {
        if (
          window.latestChannelAttribute?.expires > Date.now() ||
          window.latestChannelAttribute?.expires === -1
        ) {
          return window.latestChannelAttribute?.pub_zl_page || undefined;
        }
        return undefined;
      },
      pub_landing_page: () => {
        return window.latestChannelAttribute?.pub_landing_page || undefined;
      },
    };
    for (const key in params) {
      // 虽说不再报警告了但是值失去了实时性
      // if (typeof params[key] === 'function') {
      //   params[key] = params[key]();
      // }
      if (params[key] === undefined || params[key] === null || params[key] === '') {
        delete params[key];
      }
    }
    sensors.registerPage(params);
  }

  /**
   * 启用Sensors日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Sensors日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }
}
